import * as XLSX from 'xlsx';

const MONTH_ABBREVIATIONS = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC'
];

const DATE_FIELD_KEYWORDS = [
  'date',
  'dob',
  'birth',
  'expiry',
  'expired',
  'timestamp',
  'time'
];

const DATE_FIELD_PHRASES = [
  'created at',
  'updated at',
  'reviewed at',
  'selected at',
  'rejected at',
  'granted at',
  'issued at',
  'last status change'
];

const normalizeKeyForDateDetection = (key = '') =>
  key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .toLowerCase();

const shouldFormatAsDate = (key = '') => {
  if (!key) return false;
  const normalizedKey = normalizeKeyForDateDetection(key);

  const matchesKeyword = DATE_FIELD_KEYWORDS.some(keyword =>
    normalizedKey.includes(keyword)
  );

  if (matchesKeyword) return true;

  return DATE_FIELD_PHRASES.some(phrase => normalizedKey.includes(phrase));
};

/**
 * Excel Export Utility for Biodata Management
 * Provides comprehensive export functionality with proper formatting and data mapping
 */

/**
 * Format date to DD-MMM-YYYY (uppercase month) with fallback
 * @param {Date|string} date - Date object or string
 * @returns {string} Formatted date string
 */
const formatDate = (date) => {
  if (!date) return '';
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(dateObj.getTime())) {
      return String(date).trim().toUpperCase();
    }
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = MONTH_ABBREVIATIONS[dateObj.getMonth()] || '';
    const year = dateObj.getFullYear();
    if (!month) {
      return String(date).trim().toUpperCase();
    }
    return `${day}-${month}-${year}`;
  } catch (error) {
    console.warn('Date formatting error:', error);
    return String(date).trim().toUpperCase();
  }
};

/**
 * Format number with proper decimal places
 * @param {number|string} value - Number to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {number|string} Formatted number
 */
const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || value === '') return '';
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return decimals === 0 ? Math.round(num) : parseFloat(num.toFixed(decimals));
};

/**
 * Clean and format text data
 * @param {string} text - Text to clean
 * @returns {string} Cleaned text
 */
const cleanText = (text) => {
  if (text === null || text === undefined) return '';
  return String(text).trim();
};

const normalizeExportValue = (key, value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (value instanceof Date) {
    return formatDate(value);
  }

  if (shouldFormatAsDate(key)) {
    return formatDate(value);
  }

  if (Array.isArray(value)) {
    return value
      .map(item => (item === null || item === undefined ? '' : String(item)))
      .join(', ')
      .trim()
      .toUpperCase();
  }

  if (typeof value === 'string') {
    return value.trim().toUpperCase();
  }

  return value;
};

const normalizeExportRow = (row = {}) => {
  if (!row || typeof row !== 'object') return row;

  return Object.entries(row).reduce((acc, [key, value]) => {
    acc[key] = normalizeExportValue(key, value);
    return acc;
  }, {});
};

/**
 * Auto-size columns based on content
 * @param {Array} data - Array of objects to analyze
 * @param {Object} columnWidths - Custom column width overrides
 * @returns {Array} Column width configuration
 */
const autoSizeColumns = (data, columnWidths = {}) => {
  if (!data || data.length === 0) return [];
  
  const headers = Object.keys(data[0]);
  const maxWidths = {};
  
  // Analyze header widths
  headers.forEach(header => {
    maxWidths[header] = header.length;
  });
  
  // Analyze data widths
  data.forEach(row => {
    headers.forEach(header => {
      const value = String(row[header] || '');
      maxWidths[header] = Math.max(maxWidths[header], value.length);
    });
  });
  
  // Apply custom overrides and create column config
  return headers.map(header => ({
    wch: Math.min(Math.max(maxWidths[header], columnWidths[header] || 0), 50) // Cap at 50 characters
  }));
};

/**
 * Create Excel workbook from data with proper formatting
 * @param {Array} data - Array of data objects
 * @param {Object} options - Export options
 * @returns {Object} Excel workbook object
 */
const createExcelWorkbook = (data, options = {}) => {
  const {
    sheetName = 'Data',
    headers = {},
    columnWidths = {},
    dateFormat = 'mm/dd/yyyy',
    numberFormat = '0.00',
    includeTotals = false,
    totalsRow = []
  } = options;
  
  if (!data || data.length === 0) {
    throw new Error('No data to export');
  }
  
  // Create worksheet
  const worksheet = XLSX.utils.json_to_sheet(data, {
    header: Object.keys(data[0]),
    skipHeader: false
  });
  
  // Auto-size columns
  worksheet['!cols'] = autoSizeColumns(data, columnWidths);
  
  // Apply custom headers if provided
  if (Object.keys(headers).length > 0) {
    const headerRow = XLSX.utils.decode_range(worksheet['!ref']).s.r;
    Object.keys(headers).forEach(colKey => {
      const colIndex = Object.keys(data[0]).indexOf(colKey);
      if (colIndex !== -1) {
        const cellRef = XLSX.utils.encode_cell({ r: headerRow, c: colIndex });
        if (worksheet[cellRef]) {
          worksheet[cellRef].v = headers[colKey];
        }
      }
    });
  }
  
  // Create workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  
  return workbook;
};

/**
 * Download Excel file
 * @param {Object} workbook - Excel workbook object
 * @param {string} filename - Filename for download
 */
const downloadExcelFile = (workbook, filename) => {
  try {
    XLSX.writeFile(workbook, filename);
    return { success: true, filename };
  } catch (error) {
    console.error('Excel download error:', error);
    throw new Error(`Failed to download Excel file: ${error.message}`);
  }
};

/**
 * Export biodata to Excel with comprehensive formatting
 * @param {Array} biodataData - Array of biodata objects from API
 * @param {Object} options - Export options
 * @returns {Promise<Object>} Export result
 */
export const exportBiodataToExcel = async (biodataData, options = {}) => {
  const {
    filename = `biodata_export_${new Date().toISOString().split('T')[0]}.xlsx`,
    statusFilter = 'all',
    includeDocuments = false,
    includePhotos = false,
    isPreformatted = false // Flag to indicate if data is already formatted by backend
  } = options;

  try {
    if (!biodataData || biodataData.length === 0) {
      throw new Error('No biodata to export');
    }

    let formattedData;

    // Check if data is already formatted by backend (has "Full Name", "Passport No" etc as keys)
    const firstItem = biodataData[0];
    const hasBackendFormat = firstItem && ('Full Name' in firstItem || 'Passport No' in firstItem);

    if (hasBackendFormat || isPreformatted) {
      // Data is already formatted by backend, use it directly
      formattedData = biodataData;
    } else {
      // Format biodata for Excel export (legacy format for frontend-only processing)
      formattedData = biodataData.map(biodata => {
        const primaryNextOfKin = biodata.nextOfKin?.[0] || {};
        const latestMedical = biodata.medicals?.[0] || {};
        const latestRejection = biodata.rejections?.[0] || {};
        const latestStatusLog = biodata.statusLogs?.[0] || {};

        return {
          // Basic Information
          'ID': biodata.id,
          'Worker ID': cleanText(biodata.workerId),
          'Full Name': cleanText(biodata.fullName),
          'Passport No': cleanText(biodata.passportNo),
          'Gender': cleanText(biodata.gender),
          'Date of Birth': formatDate(biodata.dateOfBirth),
          'Age': biodata.dateOfBirth ? Math.floor((new Date().getTime() - new Date(biodata.dateOfBirth).getTime()) / (365.25 * 24 * 60 * 60 * 1000)) : '',
          'Nationality': cleanText(biodata.nationality),
          'Place of Birth': cleanText(biodata.placeOfBirth),
          'Marital Status': cleanText(biodata.maritalStatus),
          'Address': cleanText(biodata.address),
          'Email': cleanText(biodata.email),
          'Contact No': cleanText(biodata.contactNo),

          // Physical Attributes
          'Height (cm)': formatNumber(biodata.heightCm, 0),
          'Weight (kg)': formatNumber(biodata.weightKg, 1),
          'Religion': cleanText(biodata.religion),

          // Education & Work
          'Education Level': cleanText(biodata.educationLevel),
          'Experience': cleanText(biodata.experience),
          'Skills': cleanText(biodata.skill),
          'Remarks': cleanText(biodata.remarks),

          // Banking Information
          'Bank Account No': cleanText(biodata.bankAccountNo),
          'Bank Name': cleanText(biodata.bankName),

          // Medical Information
          'Medical Status': cleanText(latestMedical.medical_status || biodata.medicalStatus),
          'Medical Checkup Date': formatDate(latestMedical.medical_checkup_date),
          'Medical Expiry Date': formatDate(latestMedical.medical_expiry_date),
          'Medical Center': cleanText(latestMedical.medical_center),
          'Medical Certificate No': cleanText(latestMedical.certificate_number),

          // VDR Information
          'VDR Type': cleanText(biodata.vdrType),
          'Status': cleanText(biodata.status),

          // Next of Kin Information
          'NOK Name': cleanText(primaryNextOfKin.name),
          'NOK Relationship': cleanText(primaryNextOfKin.relationship),
          'NOK ID No': cleanText(primaryNextOfKin.identification_no),
          'NOK Address': cleanText(primaryNextOfKin.address),
          'NOK Contact No': cleanText(primaryNextOfKin.contact_no),
          'NOK Bank Account': cleanText(primaryNextOfKin.bank_account_no),
          'NOK Bank Name': cleanText(primaryNextOfKin.bank_name),

          // Recruiter Information
          'Recruiter Name': cleanText(biodata.recruiter?.name),
          'Recruiter Agency': cleanText(biodata.recruiter?.agency_name),
          'Recruiter Contact': cleanText(biodata.recruiter?.contact_no),
          'Recruiter Email': cleanText(biodata.recruiter?.email),

          // Review Information
          'Reviewed By': cleanText(biodata.reviewedByUser?.fullName),
          'Reviewer Email': cleanText(biodata.reviewedByUser?.email),
          'Reviewed At': formatDate(biodata.reviewedAt),

          // Selection Information
          'Selected By': cleanText(biodata.selectedBy),
          'Selected At': formatDate(biodata.selectedAt),

          // Rejection Information (if applicable)
          'Rejection Reason': cleanText(latestRejection.reason),
          'Rejection Remarks': cleanText(latestRejection.remarks),
          'Rejected By': cleanText(latestRejection.rejectedBy?.fullName),
          'Rejected At': formatDate(latestRejection.rejected_at),

          // Status History
          'Previous Status': cleanText(latestStatusLog.old_status),
          'Current Status': cleanText(latestStatusLog.new_status || biodata.status),
          'Status Change Notes': cleanText(latestStatusLog.notes),
          'Last Status Change': formatDate(latestStatusLog.created_at),

          // Document Information (if requested)
          ...(includeDocuments && {
            'Total Documents': biodata.documents?.length || 0,
            'Document Types': (biodata.documents?.map(d => d.document_type).join(', ') || ''),
          }),

          // Photo Information (if requested)
          ...(includePhotos && {
            'Total Photos': biodata.photos?.length || 0,
            'Photo Types': (biodata.photos?.map(p => p.photo_type).join(', ') || ''),
          }),

          // System Information
          'Created At': formatDate(biodata.createdAt),
          'Updated At': formatDate(biodata.updatedAt)
        };
      });
    }

    // Ensure consistent uppercase values and date formatting
    const normalizedData = formattedData.map(row => normalizeExportRow(row));

    // Create workbook with custom options
    const workbook = createExcelWorkbook(normalizedData, {
      sheetName: `Biodata_${statusFilter}`,
      columnWidths: {
        'Full Name': 25,
        'Address': 30,
        'Experience': 40,
        'Skills': 35,
        'Remarks': 30,
        'NOK Address': 30,
        'Rejection Reason': 25,
        'Rejection Remarks': 25,
        'Status Change Notes': 25
      }
    });

    // Download the file
    const result = downloadExcelFile(workbook, filename);

    return {
      success: true,
      filename: result.filename,
      totalRecords: normalizedData.length,
      exportedFields: Object.keys(normalizedData[0] || {}),
      timestamp: new Date().toISOString()
    };

  } catch (error) {
    console.error('Biodata export error:', error);
    throw new Error(`Failed to export biodata: ${error.message}`);
  }
};

/**
 * Export data to CSV format
 * @param {Array} data - Array of data objects
 * @param {Object} options - Export options
 * @returns {string} CSV content
 */
export const exportToCSV = (data, options = {}) => {
  const {
    filename = `export_${new Date().toISOString().split('T')[0]}.csv`,
    delimiter = ','
  } = options;
  
  if (!data || data.length === 0) {
    throw new Error('No data to export');
  }
  
  const headers = Object.keys(data[0]);
  
  // Create CSV content
  const csvContent = [
    headers.join(delimiter),
    ...data.map(row => 
      headers.map(header => {
        const value = String(row[header] || '');
        // Escape quotes and wrap in quotes if contains delimiter or quote
        if (value.includes(delimiter) || value.includes('"')) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(delimiter)
    )
  ].join('\n');
  
  return csvContent;
};

/**
 * Show export progress notification
 * @param {Function} showNotification - Notification function
 * @param {string} status - Export status ('start', 'success', 'error')
 * @param {Object} data - Additional data for notification
 */
export const showExportNotification = (showNotification, status, data = {}) => {
  const resolveScopeLabel = (scope) => {
    switch (scope) {
      case 'all':
        return 'all records';
      case 'filtered':
        return 'filtered records';
      case 'current_page':
        return 'current page';
      default:
        return scope || 'records';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || Number.isNaN(seconds)) return '';
    const rounded = Math.max(1, Math.round(seconds));
    return ` (~${rounded}s)`;
  };

  const notifications = {
    start: {
      type: 'info',
      message: `Preparing export of ${data.totalRecords ? `${data.totalRecords} ` : ''}${resolveScopeLabel(data.scope)}...`
    },
    progress: {
      type: 'info',
      message: `Exporting ${data.totalRecords || 'your'} ${resolveScopeLabel(data.scope)}${formatDuration(data.estimatedSeconds)}...`
    },
    success: {
      type: 'success',
      message: `Successfully exported ${data.totalRecords || data.total || 0} records to ${data.filename || 'download'}`
    },
    error: {
      type: 'error',
      message: `Export failed: ${data.error || 'Please try again'}`
    }
  };
  
  const notification = notifications[status];
  if (showNotification && notification) {
    showNotification(notification.type, notification.message);
  }
};

/**
 * Validate export data
 * @param {Array} data - Data to validate
 * @param {Object} options - Validation options
 * @returns {Object} Validation result
 */
export const validateExportData = (data, options = {}) => {
  const {
    maxRecords = 10000,
    requiredFields = []
  } = options;
  
  const errors = [];
  const warnings = [];
  
  if (!data || data.length === 0) {
    errors.push('No data to export');
  }
  
  if (data.length > maxRecords) {
    errors.push(`Export exceeds maximum record limit of ${maxRecords}`);
  }
  
  if (data.length > 5000) {
    warnings.push('Large dataset detected. Export may take longer to process.');
  }
  
  // Check required fields
  if (requiredFields.length > 0 && data.length > 0) {
    const firstRecord = data[0];
    const missingFields = requiredFields.filter(field => !(field in firstRecord));
    if (missingFields.length > 0) {
      errors.push(`Missing required fields: ${missingFields.join(', ')}`);
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    recordCount: data.length
  };
};

// Export all utility functions
export default {
  exportBiodataToExcel,
  exportToCSV,
  formatDate,
  formatNumber,
  cleanText,
  autoSizeColumns,
  createExcelWorkbook,
  downloadExcelFile,
  showExportNotification,
  validateExportData
};
